'use client';
import React from 'react';
import subDays from 'date-fns/subDays';

import Layout from '../lib/components/Layout';
import CalendarService from '../lib/services/CalendarService';
import ColumnsDefinition from './ColumnsDef';
import { formatRequestDate, injectLogos } from '../lib/utils';

import { ICalendarProps, IDateRange } from '../lib/interfaces';
import { fetchSpac } from './Services';
import { IPOs } from '@benzinga/calendar-manager';

export const fetchSPAC = fetchSpac;
export const SPACColumnsDefinition = ColumnsDefinition;

export const SpacCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  hasStockTableStyling,
  height,
  initialData,
  initialDateRange,
  initialTicker,
  leftSlot,
  onReady,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [tableData, setTableData] = React.useState<IPOs[]>(initialData || []);
  let dateRange: IDateRange;

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData);
    }
  }, [initialData, calendarData]);

  if (initialDateRange) {
    dateRange = initialDateRange;
  } else {
    dateRange = {
      date_from: formatRequestDate(subDays(new Date(), 30)),
      date_to: formatRequestDate(new Date()),
    };
  }

  const fetchData = React.useCallback((filterParams: any) => {
    const query = {
      ...filterParams,
      pagesize: 1000,
      'parameters[ipo_type]': 'SPAC',
    };

    setLoading(true);

    const Calendar = new CalendarService();

    Calendar.getIPOs(query)
      .then(({ data }: any) => {
        const items: any = [];
        if (Array.isArray(data.ipos) && data.ipos.length) {
          data.ipos = data.ipos.filter((item: any) => item.ipo_type === 'SPAC').reverse();
          return injectLogos(data.ipos);
        }
        return items;
      })
      .then((items: any) => {
        setTableData(items);
        setLoading(false);
      });
  }, []);

  const onTableReady = React.useCallback(() => {
    if (initialData) {
      setTableData(initialData);
    } else {
      const params = {
        'parameters[date_from]': dateRange.date_from,
        'parameters[date_to]': dateRange.date_to,
      };
      if (initialTicker) {
        params['parameters[tickers]'] = initialTicker;
      }
      fetchData(params);
    }

    if (onReady) {
      setTimeout(() => onReady());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.date_from, dateRange.date_to, fetchData, initialData, initialTicker]);

  return (
    <Layout
      calendar={calendar}
      calendarData={calendarData}
      columnDefs={ColumnsDefinition}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      initialDateRange={dateRange}
      leftSlot={leftSlot}
      onFilterChanged={fetchData}
      onTableReady={onTableReady}
      tableData={tableData}
      tableLoading={loading}
      ticker={initialTicker}
    />
  );
};
